// src/client/game/core/ammo/AmmoClip.ts

import type { TailSegment } from "../tail/TailSegment";
import type { TailModel } from "../tail/TailModel";
import { SegmentSkin } from "../tail/SegmentSkin";
import type { ConsumePolicy } from "./ConsumePolicy";

export interface ChargeRef<TCharge> {
  segmentIndex: number;
  segment: TailSegment<TCharge>;
  charge: TCharge;
}

/**
 * AmmoClip is a query/consume view over the tail segments.
 *
 * It does not create projectiles or modify gameplay rules by itself;
 * it only finds and consumes charges stored on tail segments.
 */
export class AmmoClip<TCharge = unknown> {
  constructor(private readonly tail: TailModel<TCharge>) {}

  /** All charged segments that are eligible under the given policy. */
  listEligible(policy: ConsumePolicy): ChargeRef<TCharge>[] {
    const segs = this.tail.getSegments();
    const out: ChargeRef<TCharge>[] = [];
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i];
      if (seg.charge == null) continue;
      if (!policy.isEligible(i, segs.length)) continue;
      out.push({ segmentIndex: i, segment: seg, charge: seg.charge });
    }
    return out;
  }

  /**
   * Consume the most tailward eligible charge (closest to tail tip).
   * Returns null if none are eligible.
   *
   * If you want different selection logic later, add a SelectionStrategy.
   */
  consume(policy: ConsumePolicy, spentSkin: SegmentSkin = SegmentSkin.Spent): ChargeRef<TCharge> | null {
    const eligible = this.listEligible(policy);
    if (eligible.length === 0) return null;

    // Choose the charge closest to the tail tip.
    eligible.sort((a, b) => b.segmentIndex - a.segmentIndex);
    const chosen = eligible[0];

    // Mutate the tail model: the segment becomes a normal segment with a different skin hint.
    this.tail.clearCharge(chosen.segmentIndex, spentSkin);

    return chosen;
  }
}
